/**
 * The only part of the chart alerts (drawings / indicators, chartAlerts.ts) in the entry chunk
 * (audit S-12 budget). Chart alert rules are evaluated by open charts, and every chart is itself a
 * lazy chunk, so the rules module (with the indicator registry and the drawings model it needs)
 * loads beside them instead of before the first frame.
 *
 * The shell asks for the alerts to run (`runChartAlerts`, ref-counted) and the module is imported.
 * Whoever loads it first (this gate, a chart, the Alerts panel) announces it (`chartAlertsLoaded`),
 * so a failed import here still starts the alerts as soon as a chart brings the module in. Alerts
 * never trade; a failed load affects only chart alerts.
 */

type ChartAlertsModule = { startChartAlerts: () => () => void };

let mod: ChartAlertsModule | null = null;
let wanted = 0;
let stop: (() => void) | null = null;
let importing = false;

function sync(): void {
  if (mod === null) return;
  if (wanted > 0 && stop === null) stop = mod.startChartAlerts();
  else if (wanted === 0 && stop !== null) {
    stop();
    stop = null;
  }
}

/** Called once by chartAlerts.ts when it evaluates, whoever imported it. */
export function chartAlertsLoaded(m: ChartAlertsModule): void {
  mod = m;
  sync();
}

/** Evaluate chart alerts while the caller is mounted (desktop shell). Returns the stop function. */
export function runChartAlerts(): () => void {
  wanted++;
  if (mod === null && !importing) {
    importing = true;
    import("./chartAlerts").then(
      () => {
        importing = false;
      },
      (e: unknown) => {
        importing = false;
        console.error("[lazy] chart alerts failed", e);
      },
    );
  }
  sync();
  return () => {
    wanted--;
    sync();
  };
}
